package com.schoolconnect.app.designsystem

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.check_circle_filled
import ischoolconnect.sharedui.generated.resources.trash
import org.jetbrains.compose.resources.DrawableResource

internal enum class AppConfirmationDialogTone {
    Danger,
    Primary
}

@Composable
internal fun AppConfirmationDialog(
    onDismiss: () -> Unit,
    title: String,
    subtitle: String,
    confirmLabel: String,
    onConfirm: () -> Unit,
    modifier: Modifier = Modifier,
    bodyText: String? = null,
    tone: AppConfirmationDialogTone = AppConfirmationDialogTone.Danger,
    icon: DrawableResource? = null,
    iconContentDescription: String? = null,
    dismissLabel: String = "Cancel",
    confirmEnabled: Boolean = true,
    dismissEnabled: Boolean = true,
    content: (@Composable ColumnScope.() -> Unit)? = null
) {
    val dismissAfterInteraction: () -> Unit = onDismiss
    val colors = rememberConfirmationDialogColors(tone)
    val dialogIcon = icon ?: appConfirmationDialogDefaultIcon(tone)
    val dialogIconDescription = iconContentDescription ?: when (tone) {
        AppConfirmationDialogTone.Danger -> "Warning"
        AppConfirmationDialogTone.Primary -> "Confirmation"
    }
    AppDialogScaffold(
        onDismiss = dismissAfterInteraction,
        modifier = modifier,
        size = AppDialogSize.Narrow,
        colors = colors,
        maxDialogHeight = 520.dp
    ) {
        AppDialogHeader(
            title = title,
            subtitle = subtitle,
            onClose = dismissAfterInteraction,
            icon = dialogIcon,
            iconContentDescription = dialogIconDescription,
            colors = colors,
            iconShape = CircleShape,
            iconTileSize = 56.dp,
            iconSize = 28.dp
        )
        AppDialogDivider(colors = colors)
        AppDialogBody(
            colors = colors,
            horizontalPadding = 28.dp,
            compactHorizontalPadding = 20.dp,
            verticalPadding = 24.dp
        ) { compact ->
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(if (compact) 14.dp else 16.dp),
                horizontalAlignment = androidx.compose.ui.Alignment.CenterHorizontally
            ) {
                if (!bodyText.isNullOrBlank()) {
                    Text(
                        text = bodyText,
                        color = colors.navy,
                        fontSize = 14.sp,
                        lineHeight = 20.sp,
                        fontWeight = FontWeight.Normal,
                        modifier = Modifier.fillMaxWidth(),
                        textAlign = androidx.compose.ui.text.style.TextAlign.Center,
                        maxLines = 6,
                        overflow = TextOverflow.Ellipsis
                    )
                }
                content?.invoke(this)
            }
        }
        AppDialogDivider(colors = colors)
        AppDialogFooter(
            actions = listOf(
                AppDialogFooterAction(
                    label = dismissLabel,
                    onClick = dismissAfterInteraction,
                    enabled = dismissEnabled,
                    style = AppDialogActionStyle.Secondary
                ),
                AppDialogFooterAction(
                    label = confirmLabel,
                    onClick = onConfirm,
                    enabled = confirmEnabled,
                    style = AppDialogActionStyle.Primary
                )
            ),
            colors = colors,
            equalWeightButtons = true,
            contentPadding = androidx.compose.foundation.layout.PaddingValues(horizontal = 24.dp),
            horizontalArrangement = Arrangement.End,
            footerHeight = 84.dp
        )
    }
}

private fun rememberConfirmationDialogColors(tone: AppConfirmationDialogTone): AppDialogColors {
    return when (tone) {
        AppConfirmationDialogTone.Danger -> AppDialogDefaultColors.copy(
            primary = AppDialogDefaultColors.danger,
            iconTile = Color(0xFFFDEDEF),
            selectedBackground = Color(0xFFFDEDEF),
            border = Color(0xFFE8D0D4)
        )
        AppConfirmationDialogTone.Primary -> AppDialogDefaultColors.copy(
            iconTile = AppDialogDefaultColors.selectedBackground,
            selectedBackground = AppDialogDefaultColors.selectedBackground
        )
    }
}

internal fun appConfirmationDialogDefaultIcon(tone: AppConfirmationDialogTone): DrawableResource {
    return when (tone) {
        AppConfirmationDialogTone.Danger -> Res.drawable.trash
        AppConfirmationDialogTone.Primary -> Res.drawable.check_circle_filled
    }
}
